<template>
    <div class="bottom-stats-row">
        <!-- 电压统计 -->
        <div class="stat-card">
            <div class="card-header">
                <v-icon size="20" color="#000">mdi-alpha-v-circle-outline</v-icon>
                <span class="header-text">电压统计</span>
            </div>
            <div class="stat-content">
                <div class="stat-list">
                    <div class="stat-item" v-for="(item, index) in voltageStats" :key="index">
                        <span class="stat-label">{{ item.label }}</span>
                        <div class="stat-value-group">
                            <span class="stat-value">{{ item.value }}</span>
                            <span class="stat-unit" v-if="item.unit">{{ item.unit }}</span>
                        </div>
                    </div>
                </div>
                <div class="stat-chart" ref="voltageChartRef"></div>
            </div>
        </div>

        <!-- 温度统计 -->
        <div class="stat-card">
            <div class="card-header">
                <v-icon size="20" color="#000">mdi-thermometer</v-icon>
                <span class="header-text">温度统计</span>
            </div>
            <div class="stat-content">
                <div class="stat-list">
                    <div class="stat-item" v-for="(item, index) in tempStats" :key="index">
                        <span class="stat-label">{{ item.label }}</span>
                        <div class="stat-value-group">
                            <span class="stat-value">{{ item.value }}</span>
                            <span class="stat-unit" v-if="item.unit">{{ item.unit }}</span>
                        </div>
                    </div>
                </div>
                <div class="stat-chart" ref="tempChartRef"></div>
            </div>
        </div>

        <!-- 内阻统计 -->
        <div class="stat-card">
            <div class="card-header">
                <v-icon size="20" color="#000">mdi-alpha-r-circle-outline</v-icon>
                <span class="header-text">内阻统计</span>
            </div>
            <div class="stat-content">
                <div class="stat-list">
                    <div class="stat-item" v-for="(item, index) in resistanceStats" :key="index">
                        <span class="stat-label">{{ item.label }}</span>
                        <div class="stat-value-group">
                            <span class="stat-value">{{ item.value }}</span>
                            <span class="stat-unit" v-if="item.unit">{{ item.unit }}</span>
                        </div>
                    </div>
                </div>
                <div class="stat-chart" ref="resistanceChartRef"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onUnmounted, nextTick, onMounted } from 'vue'
import * as echarts from 'echarts'
import dayjs from 'dayjs'
import { initChart, fmtd } from '@/utils/index'

const props = defineProps({
    batteryData: {
        type: Object,
        default: () => ({})
    }
})

// 统计数据
const voltageStats = ref([
    { label: '最高单体电压', value: '--', unit: 'mV' },
    { label: '最高电压位置', value: '--' },
    { label: '最低单体电压', value: '--', unit: 'mV' },
    { label: '最低电压位置', value: '--' },
    { label: '最大压差', value: '--', unit: 'mV' }
])

const tempStats = ref([
    { label: '最高单体温度', value: '--', unit: '℃' },
    { label: '最高温度位置', value: '--' },
    { label: '最低单体温度', value: '--', unit: '℃' },
    { label: '最低温度位置', value: '--' },
    { label: '最大温差', value: '--', unit: '℃' }
])

const resistanceStats = ref([
    { label: '最高单体内阻', value: '--', unit: 'mΩ' },
    { label: '最高内阻位置', value: '--' },
    { label: '最低单体内阻', value: '--', unit: 'mΩ' },
    { label: '最低内阻位置', value: '--' },
    { label: '最大内阻差', value: '--', unit: 'mΩ' }
])

const voltageChartRef = ref(null)
const tempChartRef = ref(null)
const resistanceChartRef = ref(null)
let charts = []

// 生成 Pack 区域标记
const getMarkArea = (packSize, packNumber) => {
    const colorOdd = 'rgba(255, 128, 64, 0.05)'
    const colorEven = 'rgba(64, 128, 255, 0.05)'

    const data = []
    let start = 0
    let end = packSize

    for (let i = 1; i <= packNumber; i++) {
        const isOdd = i % 2 === 0

        const element = [
            {
                itemStyle: {
                    color: isOdd ? colorOdd : colorEven
                },
                name: 'Pack#' + i,
                xAxis: start - 1
            },
            {
                itemStyle: {
                    color: isOdd ? colorOdd : colorEven
                },
                name: 'Pack#' + i,
                xAxis: end - 1
            }
        ]

        start = end + 1
        end = start + packSize - 1

        data.push(element)
    }

    return {
        data: data,
        label: {
            color: '#B8B6B6',
            offset: [0, 12]
        }
    }
}

const renderChart = (el, title, yUnit, colorStart, colorEnd, yData = [], packSize = 18, packNumber = 12) => {
    if (!el) return null

    const chart = initChart(el)

    // 如果没有数据，使用模拟数据
    const hasData = yData && yData.length > 0
    const totalPoints = hasData ? yData.length : packSize * packNumber + 1
    const xData = Array.from({ length: totalPoints }, (_, i) => i + 1)
    const chartData = hasData ? yData : []//Array.from({ length: totalPoints }, () => 3300 + Math.random() * 100)

    // 获取 Pack 区域标记
    const markArea = getMarkArea(packSize, packNumber)

    // 动态计算 y 轴范围
    const minValue = Math.min(...chartData)
    const maxValue = Math.max(...chartData)
    const margin = (maxValue - minValue) * 0.1 || 10

    const option = {
        title: {
            text: title,
            right: 10,
            top: 0,
            textStyle: {
                fontSize: 12,
                color: '#666',
                fontWeight: 'normal'
            }
        },
        grid: {
            top: 30,
            left: 10,
            right: 20,
            bottom: 10,
            containLabel: true
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            },
            formatter: (params) => {
                const { name, value } = params[0]
                return `电芯 ${name}<br/>数值: ${value}${yUnit}`
            }
        },
        xAxis: {
            type: 'category',
            data: xData,
            axisLabel: {
                interval: Math.floor(totalPoints / 15),
                color: '#999',
                fontSize: 10
            },
            axisTick: { show: false },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#E0E0E0'
                }
            },
            splitLine: { show: false }
        },
        yAxis: {
            type: 'value',
            scale: true,
            min: Math.floor(minValue - margin),
            max: Math.ceil(maxValue + margin),
            axisLabel: {
                color: '#999',
                fontSize: 10,
                formatter: `{value}${yUnit}`
            },
            splitLine: {
                lineStyle: {
                    type: 'dashed',
                    color: '#E8E8E8'
                }
            },
            axisLine: { show: false }
        },
        series: [
            {
                name: '数值',
                type: 'line',
                smooth: true,
                showSymbol: false,
                data: chartData,
                lineStyle: {
                    width: 2,
                    color: colorStart
                },
                itemStyle: {
                    color: colorStart
                },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: colorStart + '80' },
                        { offset: 1, color: 'rgba(255, 255, 255, 0.1)' }
                    ])
                },
                markArea: {
                    silent: true,
                    itemStyle: {
                        color: 'rgba(249, 250, 252, 0.5)'
                    },
                    ...markArea
                }
            }
        ]
    }

    chart.setOption(option)
    return chart
}

const updateStats = (msg) => {
    if (!msg || !msg.census) return

    const census = msg.census

    // 电压统计
    voltageStats.value = [
        { label: '最高单体电压', value: fmtd(census.maxECU, 1, 1) || '--', unit: 'mV' },
        { label: '最高电压位置', value: census.maxECUIndex ? `${census.maxECUIndex}#` : '--' },
        { label: '最低单体电压', value: fmtd(census.minECU, 1, 1) || '--', unit: 'mV' },
        { label: '最低电压位置', value: census.minECUIndex ? `${census.minECUIndex}#` : '--' },
        { label: '最大压差', value: fmtd(census.maxECU - census.minECU, 1, 1) || '--', unit: 'mV' }
    ]

    // 温度统计
    tempStats.value = [
        { label: '最高单体温度', value: fmtd(census.maxECT, 1, 1) || '--', unit: '℃' },
        { label: '最高温度位置', value: census.maxECTIndex ? `${census.maxECTIndex}#` : '--' },
        { label: '最低单体温度', value: fmtd(census.minECT, 1, 1) || '--', unit: '℃' },
        { label: '最低温度位置', value: census.minECTIndex ? `${census.minECTIndex}#` : '--' },
        { label: '最大温差', value: fmtd(census.maxECT - census.minECT, 1, 1) || '--', unit: '℃' }
    ]

    // 内阻统计
    if (census.maxECR != null && census.minECR != null) {
        resistanceStats.value = [
            { label: '最高单体内阻', value: fmtd(census.maxECR, 1, 2) || '--', unit: 'mΩ' },
            { label: '最高内阻位置', value: census.maxECRIndex ? `${census.maxECRIndex}#` : '--' },
            { label: '最低单体内阻', value: fmtd(census.minECR, 1, 2) || '--', unit: 'mΩ' },
            { label: '最低内阻位置', value: census.minECRIndex ? `${census.minECRIndex}#` : '--' },
            { label: '最大内阻差', value: fmtd(census.maxECR - census.minECR, 1, 2) || '--', unit: 'mΩ' }
        ]
    }
}

const refreshCharts = (data) => {
    try {
        if (!data.bmu || !data.bmu.bmuList || !Array.isArray(data.bmu.bmuList)) {
            return
        }

        const bmuList = data.bmu.bmuList
        const packSize = data.bmu.packSize || 18 // pack 数量
        const cellNumbersInPack = Math.ceil(bmuList.length / packSize) // 一个 pack 的电芯数量

        // 提取数据
        const voltageData = bmuList.map(item => Number(item.U) || 0)
        const tempData = bmuList.map(item => Number(fmtd(item.T, 1, 2)) || 0)
        const resistanceData = bmuList.map(item => Number(fmtd(item.R, 1, 2)) || 0)

        const timeStr = data.bcs?.Time ? dayjs(data.bcs.Time).format('YYYY-MM-DD HH:mm:ss') : ''
        const timeLabel = timeStr ? `(${timeStr})` : ''

        // 更新图表
        if (charts[0]) {
            const xData = Array.from({ length: voltageData.length }, (_, i) => i + 1)
            const markArea = getMarkArea(cellNumbersInPack, packSize)
            const minV = Math.min(...voltageData.map(Number))
            const maxV = Math.max(...voltageData.map(Number))

            charts[0].setOption({
                title: { text: '单体电池电压实时曲线' + timeLabel },
                xAxis: { data: xData },
                yAxis: { min: minV - 5, max: maxV + 5 },
                series: [{
                    data: voltageData,
                    markArea: { ...markArea, silent: true, itemStyle: { color: 'rgba(249, 250, 252, 0.5)' } }
                }]
            })
        }

        if (charts[1]) {
            const xData = Array.from({ length: tempData.length }, (_, i) => i + 1)
            const markArea = getMarkArea(cellNumbersInPack, packSize)
            const minT = Math.min(...tempData.map(Number))
            const maxT = Math.max(...tempData.map(Number))

            charts[1].setOption({
                title: { text: '单体电池温度实时曲线' + timeLabel },
                xAxis: { data: xData },
                yAxis: { min: minT - 5, max: maxT + 5 },
                series: [{
                    data: tempData,
                    markArea: { ...markArea, silent: true, itemStyle: { color: 'rgba(249, 250, 252, 0.5)' } }
                }]
            })
        }

        if (charts[2] && resistanceData.length > 0) {
            const xData = Array.from({ length: resistanceData.length }, (_, i) => i + 1)
            const markArea = getMarkArea(cellNumbersInPack, packSize)
            const minR = Math.min(...resistanceData.map(Number))
            const maxR = Math.max(...resistanceData.map(Number))

            charts[2].setOption({
                title: { text: '单体电池内阻实时曲线' + timeLabel },
                xAxis: { data: xData },
                yAxis: { min: Math.max(0, minR - 0.5), max: maxR + 0.5 },
                series: [{
                    data: resistanceData,
                    markArea: { ...markArea, silent: true, itemStyle: { color: 'rgba(249, 250, 252, 0.5)' } }
                }]
            })
        }
    } catch (error) {
        console.error('[ClusterDetailChart] 图表刷新失败:', error)
    }
}

const initCharts = () => {
    if (voltageChartRef.value) {
        charts[0] = renderChart(
            voltageChartRef.value,
            '单体电池电压极值趋势',
            'mV',
            '#FF7867',
            '#FFE0E0'
        )
    }
    if (tempChartRef.value) {
        charts[1] = renderChart(
            tempChartRef.value,
            '单体电池温度极值趋势',
            '℃',
            '#548CFC',
            '#E0F0FF'
        )
    }
    if (resistanceChartRef.value) {
        charts[2] = renderChart(
            resistanceChartRef.value,
            '单体电池内阻极值趋势',
            'mΩ',
            '#A569BD',
            '#F4ECF7'
        )
    }
}

watch(() => props.batteryData, (newVal) => {
    if (newVal) {
        updateStats(newVal)
        refreshCharts(newVal)
    }
}, { deep: true, immediate: true })

onMounted(() => {
    nextTick(() => {
        initCharts()
        if (props.batteryData) {
            updateStats(props.batteryData)
            refreshCharts(props.batteryData)
        }
    })
})

onUnmounted(() => {
    charts.forEach(c => c && c.dispose())
    charts = []
})
</script>

<style scoped lang="scss">
.bottom-stats-row {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
}

.stat-card {
    height: 288px;
    background: #FFFFFF;
    border-radius: 15px;
    padding: 20px;
    display: flex;
    flex-direction: column;
}

.card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;

    .header-text {
        font-size: 16px;
        font-weight: 500;
        color: #333;
    }
}

.stat-content {
    flex: 1;
    display: flex;
    gap: 20px;
}

.stat-list {
    width: 300px;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    padding-right: 20px;
    border-right: 1px solid #F0F0F0;
}

.stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .stat-label {
        font-size: 14px;
        color: #666;
    }

    .stat-value-group {
        text-align: right;
    }

    .stat-value {
        font-size: 16px;
        color: #333;
    }

    .stat-unit {
        font-size: 12px;
        color: #999;
        margin-left: 4px;
    }
}

.stat-chart {
    flex: 1;
    height: 100%;
    width: 100%;
}
</style>

